import React, { useEffect } from 'react';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getGuidelines } from '@/api/guidelines';
import { useAuth } from '@/contexts/AuthContext';


interface GuidelineSelectorProps {
  disabled?: boolean;
  fieldName?: string;
  onGuidelineDataChange?: (guideline: { id: string; name: string } | null) => void;
}

export const GuidelineSelector: React.FC<GuidelineSelectorProps> = ({
  disabled = false,
  fieldName = 'guidelineId',
  onGuidelineDataChange
}) => {
  const { t } = useTranslation(['guidelines', 'common']);
  const { firestoreUser } = useAuth();
  const form = Form.useFormInstance();

  // Fetch guidelines
  const { data: guidelineData, error } = useQuery({
    queryKey: ['guidelines', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getGuidelines({ firestoreUser });
    },
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Initialize guideline data from form values when component mounts or form values change
  useEffect(() => {
    const guidelineId = form.getFieldValue(fieldName);
    if (guidelineId && guidelineData?.guidelines) {
      const guideline = guidelineData.guidelines.find(g => g.id === guidelineId);
      onGuidelineDataChange?.(guideline ? { 
        id: guideline.id, 
        name: `Guidelines v${guideline.version}` 
      } : null);
    }
  }, [form, guidelineData, onGuidelineDataChange, fieldName]);

  const handleGuidelineChange = (guidelineId: string) => {
    // Provide guideline data to parent
    if (guidelineId && guidelineData?.guidelines) {
      const guideline = guidelineData.guidelines.find(g => g.id === guidelineId);
      onGuidelineDataChange?.(guideline ? { 
        id: guideline.id, 
        name: `Guidelines v${guideline.version}` 
      } : null);
    } else {
      onGuidelineDataChange?.(null);
    }
  };

  const guidelineOptions = guidelineData?.guidelines ? guidelineData.guidelines.map(guideline => ({
    value: guideline.id,
    label: `Guidelines v${guideline.version}`,
  })) : [];

  // Show error state if API fails
  if (error) {
    return (
      <Form.Item
        name={fieldName}
        label={t('guidelines:fields.title')}
      >
        <Select
          placeholder={t('guidelines:fields.selectGuideline')}
          disabled={true}
          options={[]}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item
      name={fieldName}
      label={t('guidelines:fields.title')}
    >
      <Select
        placeholder={t('guidelines:fields.selectGuideline')}
        onChange={handleGuidelineChange}
        disabled={disabled}
        allowClear
        options={guidelineOptions}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
    </Form.Item>
  );
}; 